import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  DEFAULT_PREFLIGHT_REPORT_SORT_FIELD,
  DEFAULT_PREFLIGHT_REPORT_SORT_ORDER,
  defaultPreflightReportSortOrder,
  type PreflightPlatformCode,
  type PreflightReportSortField,
  type PreflightReportSortOrder,
} from "@nexus/contracts";
import {
  Button,
  EmptyState,
  Icon,
  Input,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Toggle,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  UserAvatar,
  cn,
  type IconName,
} from "@nexus/ui";
import { toast } from "sonner";
import { useAuth } from "@/features/auth/hooks/useAuth";
import {
  preflightKeys,
  preflightReportQueryOptions,
  preflightReportStatusQueryOptions,
} from "@/lib/queries";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";
import { useDebouncedCallback } from "@/hooks/useDebouncedCallback";
import { downloadReportPdf, getReport, retryReport } from "../../api/preflight.api";
import {
  exportReportToCsv,
  exportReportToJson,
  exportReportToText,
} from "../../lib/export-report";
import {
  MIDDLE_MOUSE_BUTTON,
  toOpenMode,
  type OpenPreflightReportOptions,
} from "../../lib/report-open";
import { usePreflightReports } from "../../hooks/usePreflightReports";
import {
  isTerminalReportStatus,
  type PreflightReportStatus,
  type PreflightReportSummary,
} from "../../types/preflight.types";
import { StatusBadge } from "../StatusBadge/StatusBadge";
import { ScoreBadge } from "../ScoreBadge/ScoreBadge";
import { ReportNameCell } from "../ReportNameCell/ReportNameCell";
import { ReportMarketCell } from "../ReportMarketCell/ReportMarketCell";
import { ReportFavoriteToggle } from "../ReportFavoriteToggle/ReportFavoriteToggle";
import { DeleteReportDialog } from "../DeleteReportDialog/DeleteReportDialog";
import { PlatformLabel } from "../PlatformLabel/PlatformLabel";
import { PlatformPicker } from "../PlatformPicker/PlatformPicker";
import {
  PastReportExportMenu,
  type PastReportExportFormat,
} from "../PastReportExportMenu/PastReportExportMenu";

const SEARCH_DEBOUNCE_MS = 300;

const TABLE_HEAD =
  "text-muted-foreground text-[11px] font-normal tracking-wider uppercase";

/**
 * Fixed layout so column widths come from the header alone. With auto layout the
 * browser re-measures every column against the rows currently rendered, so
 * sorting, searching or filtering visibly reflowed the whole table. The min width
 * is the sum of the column widths below — a narrow viewport scrolls horizontally
 * rather than squishing columns.
 */
const TABLE_LAYOUT = "table-fixed min-w-[76rem]";

/**
 * Widths sum to the table's min width, and each one clears the widest thing that
 * column renders — usually its own header. The sum has to stay under the hub's
 * content width (`max-w-7xl` minus page padding) or every desktop gets a
 * horizontal scrollbar by default, so names truncate rather than widen. Above
 * the min width the browser hands out the surplus proportionally.
 */
const COLUMN_WIDTH = {
  /**
   * Favorite star + name + rename pencil; the name is what people scan the list by.
   * 12rem rather than 14rem leaves room for Brand inside the 76rem budget.
   */
  report: "w-[12rem]",
  created: "w-[6.25rem]",
  creatives: "w-[5.75rem]",
  /** Brand names truncate; the width is borrowed from market, platform and score. */
  brand: "w-[6rem]",
  /** Flag + catalogue name; long names ("United Kingdom") truncate. */
  market: "w-[8rem]",
  /** Icon + longest name ("YouTube" / "Snapchat") stacked over placement. */
  platformPlacement: "w-[7.5rem]",
  avgScore: "w-[6.5rem]",
  passFail: "w-[6.25rem]",
  /** Avatar + a full display name (e.g. "Alex Morgan") without truncating. */
  owner: "w-[10rem]",
  /** Right-aligned icon buttons, so this column drops the leading cell padding. */
  actions: "w-[7.75rem] pl-0",
} as const;

/** Report, Created, Creatives, Brand, Market, Platform/Placement, Avg Score, Pass/Fail, Owner, Actions. */
const TABLE_COLUMN_COUNT = 10;

const ROW_ACTION_FADE_MS = 220;
const ROW_ACTION_STAGGER_MS = 45;

const ROW_ICON_BUTTON =
  "border-border/60 bg-background text-muted-foreground hover:border-border hover:bg-muted hover:text-foreground hover:shadow-sm active:scale-[0.96] min-h-11 min-w-11 [@media(hover:hover)_and_(pointer:fine)]:min-h-8 [@media(hover:hover)_and_(pointer:fine)]:min-w-8 shrink-0 rounded-md border shadow-xs transition-[color,background-color,border-color,box-shadow,transform] duration-150 ease-out [&_svg]:text-inherit";

const ROW_ICON_BUTTON_DESTRUCTIVE =
  "hover:border-error-border hover:bg-error-subtle hover:text-error hover:shadow-sm focus-visible:border-error-border focus-visible:text-error focus-visible:ring-error/20 [&_svg]:text-inherit";

/**
 * Up to five actions are wider than the actions column, so the toolbar is pinned to the
 * cell's right edge and grows left over the Owner cell, taking the row's own background
 * (Gmail-style hover actions). This keeps the column widths unchanged. The toolbar ignores
 * the pointer, so only the revealed buttons catch clicks. Without hover the actions are always
 * shown, so an overlay would hide the Owner cell on every row: the toolbar stays in flow there.
 */
const ROW_ACTIONS_TOOLBAR =
  "pointer-events-none absolute inset-y-0 right-0 flex items-center justify-end gap-1 bg-inherit pr-4 pl-2 [@media(hover:none)]:static";

/**
 * Quick view and Open in new tab are pointer conveniences. On touch the in-flow column only
 * fits two 44px buttons, a tap opens the full report, and the 720px peek nearly fills a tablet.
 */
const POINTER_ONLY_ROW_ACTION = "[@media(hover:none)]:hidden";

/** Opacity-only reveal; stagger on enter, simultaneous fade on leave. */
const ROW_ACTION_REVEAL =
  "pointer-events-none opacity-0 transition-opacity ease-in-out motion-reduce:transition-none group-hover/row:pointer-events-auto group-hover/row:opacity-100 group-focus-within/row:pointer-events-auto group-focus-within/row:opacity-100 [@media(hover:none)]:pointer-events-auto [@media(hover:none)]:opacity-100";

function rowActionRevealStyle(isRowHovered: boolean, delayMs: number) {
  return {
    transitionDuration: `${ROW_ACTION_FADE_MS}ms`,
    transitionDelay: isRowHovered ? `${delayMs}ms` : "0ms",
  } as const;
}

interface PastReportRowActionSlotProps {
  isRowHovered: boolean;
  delayMs: number;
  className?: string;
  children: ReactNode;
}

function PastReportRowActionSlot({
  isRowHovered,
  delayMs,
  className,
  children,
}: PastReportRowActionSlotProps) {
  return (
    <span
      className={cn("inline-flex", ROW_ACTION_REVEAL, className)}
      style={rowActionRevealStyle(isRowHovered, delayMs)}
    >
      {children}
    </span>
  );
}

interface PastReportRowActionProps {
  label: string;
  icon: IconName;
  delayMs: number;
  isRowHovered: boolean;
  onClick?: () => void;
  /** Hover or focus on the button — a head start for whatever the click will load. */
  onIntent?: () => void;
  disabled?: boolean;
  destructive?: boolean;
  iconClassName?: string;
  slotClassName?: string;
}

function PastReportRowAction({
  label,
  icon,
  delayMs,
  isRowHovered,
  onClick,
  onIntent,
  disabled,
  destructive,
  iconClassName,
  slotClassName,
}: PastReportRowActionProps) {
  return (
    <PastReportRowActionSlot
      isRowHovered={isRowHovered}
      delayMs={delayMs}
      className={slotClassName}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon-lg"
            aria-label={label}
            disabled={disabled}
            onClick={onClick}
            onPointerEnter={onIntent}
            onFocus={onIntent}
            className={cn(
              ROW_ICON_BUTTON,
              destructive && ROW_ICON_BUTTON_DESTRUCTIVE,
            )}
          >
            <Icon name={icon} size="sm" color="inherit" className={iconClassName} />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="top">{label}</TooltipContent>
      </Tooltip>
    </PastReportRowActionSlot>
  );
}

interface PastReportsSort {
  field: PreflightReportSortField;
  order: PreflightReportSortOrder;
}

const INITIAL_SORT: PastReportsSort = {
  field: DEFAULT_PREFLIGHT_REPORT_SORT_FIELD,
  order: DEFAULT_PREFLIGHT_REPORT_SORT_ORDER,
};

/** Re-sorting the active column flips it; a new column starts in its own natural direction. */
function nextSort(current: PastReportsSort, field: PreflightReportSortField): PastReportsSort {
  if (current.field !== field) {
    return { field, order: defaultPreflightReportSortOrder(field) };
  }
  return { field, order: current.order === "asc" ? "desc" : "asc" };
}

interface SortableTableHeadProps {
  field: PreflightReportSortField;
  label: string;
  sort: PastReportsSort;
  onSort: (field: PreflightReportSortField) => void;
  className?: string;
}

/** Sortable column header — the whole label is the control, and `aria-sort` carries the state. */
function SortableTableHead({
  field,
  label,
  sort,
  onSort,
  className,
}: SortableTableHeadProps) {
  const { t } = useTranslation();
  const isActive = sort.field === field;
  const activeIcon: IconName = sort.order === "asc" ? "chevron-up" : "chevron-down";

  return (
    <TableHead
      className={cn(TABLE_HEAD, className)}
      aria-sort={isActive ? (sort.order === "asc" ? "ascending" : "descending") : "none"}
    >
      <button
        type="button"
        onClick={() => onSort(field)}
        aria-label={t("preflight.pastReports.sortByColumn", { column: label })}
        className="hover:text-foreground inline-flex cursor-pointer items-center gap-1 uppercase transition-colors"
      >
        {label}
        <Icon
          name={isActive ? activeIcon : "chevrons-up-down"}
          size="xs"
          className={isActive ? undefined : "opacity-50"}
        />
      </button>
    </TableHead>
  );
}

function isRetryableStatus(status: PreflightReportStatus): boolean {
  return status === "FAILED" || status === "COMPLETED_WITH_ERRORS";
}

/** Creatives count for a list row — status poll can return 0 before finalize denormalizes. */
function resolveCreativesCount(
  report: PreflightReportSummary,
  live: { assetCount?: number } | undefined,
  isTerminal: boolean,
): number {
  const listCount = report.assetCount;
  const liveCount = live?.assetCount;
  if (!isTerminal && liveCount != null && liveCount < listCount) {
    return listCount;
  }
  return liveCount ?? listCount;
}

function formatCreatedParts(iso: string): { date: string; time: string } {
  const value = new Date(iso);
  return {
    date: value.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    }),
    time: value.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }),
  };
}

function CreatedCell({ iso }: { iso: string }) {
  const { date, time } = formatCreatedParts(iso);
  return (
    <span className="inline-flex flex-col gap-0.5 text-[11px] leading-tight tabular-nums">
      <span className="whitespace-nowrap">{date}</span>
      <span className="text-muted-foreground/75 whitespace-nowrap">{time}</span>
    </span>
  );
}

interface PlatformPlacementCellProps {
  platform: PreflightReportSummary["platform"];
  placement: PreflightReportSummary["placement"];
}

/**
 * Stacked rather than "Meta · Reels" on one line: side by side the pair was the
 * table's widest column after the report name, and the column has to fit a
 * viewport that already shows eight others.
 */
function PlatformPlacementCell({ platform, placement }: PlatformPlacementCellProps) {
  const { t } = useTranslation();

  return (
    <span className="flex flex-col gap-0.5 leading-tight">
      <PlatformLabel platform={platform} className="text-foreground text-sm" />
      <span className="text-muted-foreground truncate text-[11px]">
        {t(`preflight.placement.${placement}`)}
      </span>
    </span>
  );
}

interface PassFailCellProps {
  passedCount: number;
  failedCount: number;
  isTerminal: boolean;
}

interface AvgScoreCellProps {
  status: PreflightReportStatus;
  score: number | null;
  band: PreflightReportSummary["overallBand"];
}

/**
 * Avg Score per past-reports R2 — in-progress and failed reports have no score,
 * so they show their status badge here instead. A partial analysis still has a
 * score, so its caveat is flagged on the name (see `PartialAnalysisMarker`).
 */
function AvgScoreCell({ status, score, band }: AvgScoreCellProps) {
  if (!isTerminalReportStatus(status) || status === "FAILED") {
    return <StatusBadge status={status} />;
  }

  return <ScoreBadge score={score} band={band} />;
}

/** Keeps the partial-analysis caveat out of the score column, which must stay one line. */
function PartialAnalysisMarker() {
  const { t } = useTranslation();
  const label = t("preflight.pastReports.partialAnalysis");

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="text-warning inline-flex shrink-0 items-center">
          <Icon name="alert-circle" size="xs" color="inherit" />
          <span className="sr-only">{label}</span>
        </span>
      </TooltipTrigger>
      <TooltipContent side="top">{label}</TooltipContent>
    </Tooltip>
  );
}

/**
 * Two independent counts, not a fraction — each gets an icon so the pass/fail
 * meaning survives without colour, plus a spelled-out label for screen readers.
 */
function PassFailCell({ passedCount, failedCount, isTerminal }: PassFailCellProps) {
  const { t } = useTranslation();

  if (!isTerminal || (passedCount === 0 && failedCount === 0)) {
    return (
      <span className="text-muted-foreground tabular-nums">
        {t("preflight.pastReports.passFailEmpty")}
      </span>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex items-center gap-2.5 text-sm tabular-nums">
          <span className="text-success inline-flex items-center gap-1">
            <Icon name="check" size="xs" color="inherit" />
            {passedCount}
          </span>
          <span
            className={cn(
              "inline-flex items-center gap-1",
              failedCount > 0 ? "text-error" : "text-muted-foreground",
            )}
          >
            <Icon name="x" size="xs" color="inherit" />
            {failedCount}
          </span>
          <span className="sr-only">
            {t("preflight.pastReports.passFailAria", {
              pass: passedCount,
              fail: failedCount,
            })}
          </span>
        </span>
      </TooltipTrigger>
      <TooltipContent side="top">
        {t("preflight.pastReports.passFailTooltip", {
          pass: passedCount,
          fail: failedCount,
          total: passedCount + failedCount,
        })}
      </TooltipContent>
    </Tooltip>
  );
}

interface OwnerCellProps {
  owner: PreflightReportSummary["createdBy"];
}

function OwnerCell({ owner }: OwnerCellProps) {
  const { t } = useTranslation();

  if (!owner) {
    return (
      <span className="text-muted-foreground">
        {t("preflight.pastReports.passFailEmpty")}
      </span>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="flex w-full min-w-0 items-center gap-2">
          <UserAvatar name={owner.displayName} src={owner.photoUrl} size="sm" />
          <span className="text-foreground truncate text-sm">{owner.displayName}</span>
        </span>
      </TooltipTrigger>
      <TooltipContent side="top">{owner.displayName}</TooltipContent>
    </Tooltip>
  );
}

interface PastReportRowActionsProps {
  report: PreflightReportSummary;
  isRowHovered: boolean;
  isTerminal: boolean;
  status: PreflightReportStatus;
  retryPending: boolean;
  exportPending: boolean;
  onOpenReport: (id: string, options?: OpenPreflightReportOptions) => void;
  onPrefetchReport: () => void;
  onRetry: () => void;
  onExport: (format: PastReportExportFormat) => void;
}

function PastReportRowActions({
  report,
  isRowHovered,
  isTerminal,
  status,
  retryPending,
  exportPending,
  onOpenReport,
  onPrefetchReport,
  onRetry,
  onExport,
}: PastReportRowActionsProps) {
  const { t } = useTranslation();
  const canRetry = isRetryableStatus(status);
  const canExport = isTerminal && status !== "FAILED";
  let actionIndex = 0;

  function handleExport(format: PastReportExportFormat) {
    onExport(format);
  }

  return (
    <div className={ROW_ACTIONS_TOOLBAR}>
      {isTerminal ? (
        <>
          <PastReportRowAction
            label={t("preflight.pastReports.actionQuickView")}
            icon="panel-right-open"
            isRowHovered={isRowHovered}
            delayMs={actionIndex++ * ROW_ACTION_STAGGER_MS}
            slotClassName={POINTER_ONLY_ROW_ACTION}
            onIntent={onPrefetchReport}
            onClick={() => onOpenReport(report.id, { mode: "peek" })}
          />
          <PastReportRowAction
            label={t("preflight.pastReports.actionOpenNewTab")}
            icon="external-link"
            isRowHovered={isRowHovered}
            delayMs={actionIndex++ * ROW_ACTION_STAGGER_MS}
            slotClassName={POINTER_ONLY_ROW_ACTION}
            onClick={() => onOpenReport(report.id, { mode: "new-tab" })}
          />
        </>
      ) : null}

      {canExport ? (
        <PastReportExportMenu
          exportPending={exportPending}
          revealClassName={ROW_ACTION_REVEAL}
          revealStyle={rowActionRevealStyle(
            isRowHovered,
            actionIndex++ * ROW_ACTION_STAGGER_MS,
          )}
          onExport={handleExport}
        />
      ) : null}

      {canRetry ? (
        <PastReportRowAction
          label={t("preflight.pastReports.actionRetry")}
          icon="refresh-cw"
          isRowHovered={isRowHovered}
          delayMs={actionIndex++ * ROW_ACTION_STAGGER_MS}
          disabled={retryPending}
          onClick={onRetry}
          iconClassName={retryPending ? "animate-spin" : undefined}
        />
      ) : null}

      <PastReportRowActionSlot
        isRowHovered={isRowHovered}
        delayMs={actionIndex * ROW_ACTION_STAGGER_MS}
      >
        <DeleteReportDialog
          report={report}
          trigger={
            <Button
              type="button"
              variant="ghost"
              size="icon-lg"
              className={cn(ROW_ICON_BUTTON, ROW_ICON_BUTTON_DESTRUCTIVE)}
              aria-label={t("preflight.delete.trigger")}
            >
              <Icon name="trash-2" size="sm" color="inherit" />
            </Button>
          }
        />
      </PastReportRowActionSlot>
    </div>
  );
}

interface PastReportRowProps {
  report: PreflightReportSummary;
  /** The report open in the hub's side peek gets the table's selected-row treatment. */
  isPeeked: boolean;
  onOpenReport: (id: string, options?: OpenPreflightReportOptions) => void;
  onTerminal: () => void;
}

/** A single row that live-polls its status while non-terminal and flips in place (generate-status R3/R5). */
function PastReportRow({
  report,
  isPeeked,
  onOpenReport,
  onTerminal,
}: PastReportRowProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const activeTenantId = useAuth((s) => s.activeTenantId);
  const [isRowHovered, setIsRowHovered] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const pollStartedAtRef = useRef(0);
  const statusFromList: PreflightReportStatus = report.status;
  if (
    !isTerminalReportStatus(statusFromList) &&
    pollStartedAtRef.current === 0
  ) {
    pollStartedAtRef.current = Date.now();
  }

  const statusQuery = useQuery(
    preflightReportStatusQueryOptions(
      activeTenantId,
      report.id,
      pollStartedAtRef.current,
    ),
  );

  const live = statusQuery.data;
  const status: PreflightReportStatus = live?.status ?? report.status;
  const score = live?.overallScore ?? report.overallScore;
  const band = live?.overallBand ?? report.overallBand;
  const assetCount = resolveCreativesCount(report, live, isTerminalReportStatus(status));

  const retryMutation = useMutation({
    mutationFn: () => retryReport(report.id),
    onSuccess: () => {
      pollStartedAtRef.current = 0;
      onTerminal();
      toast.success(t("preflight.toast.rerunning"));
    },
    onError: () => toast.error(t("preflight.toast.retryError")),
    meta: { skipGlobalError: true },
  });

  const exportMutation = useMutation({
    mutationFn: async (format: "csv" | "json" | "text" | "pdf") => {
      if (format === "pdf") {
        await downloadReportPdf(report.id);
        return;
      }
      const detail = await getReport(report.id);
      if (format === "csv") exportReportToCsv(detail);
      else if (format === "json") exportReportToJson(detail);
      else exportReportToText(detail);
    },
    onError: () => toast.error(t("preflight.toast.downloadError")),
    meta: { skipGlobalError: true },
  });

  useEffect(() => {
    if (live && isTerminalReportStatus(live.status)) onTerminal();
  }, [live?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const isClickable = isTerminalReportStatus(status);
  const isTerminal = isTerminalReportStatus(status);

  /** Quick view renders the detail query, so warming it on intent makes the peek open filled. */
  const prefetchReport = () => {
    if (!activeTenantId) return;
    void queryClient.prefetchQuery(
      preflightReportQueryOptions(activeTenantId, report.id),
    );
  };

  /** Enter opens the page (Cmd/Ctrl+Enter a new tab); Space peeks, as in list UIs with a preview pane. */
  const handleRowKeyDown = (event: KeyboardEvent<HTMLTableRowElement>) => {
    if (!isClickable) return;
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter") {
      event.preventDefault();
      onOpenReport(report.id, { mode: toOpenMode(event) });
      return;
    }
    if (event.key === " ") {
      event.preventDefault();
      onOpenReport(report.id, { mode: "peek" });
    }
  };

  // Row dialogs (e.g. change market) portal to document.body, but React still
  // bubbles their clicks through the component tree to this row.
  const isOwnClick = (event: MouseEvent<HTMLTableRowElement>) =>
    event.currentTarget.contains(event.target as Node);

  const handleRowClick = (event: MouseEvent<HTMLTableRowElement>) => {
    if (!isOwnClick(event)) return;
    onOpenReport(report.id, { mode: toOpenMode(event) });
  };

  const handleRowAuxClick = (event: MouseEvent<HTMLTableRowElement>) => {
    if (event.button !== MIDDLE_MOUSE_BUTTON || !isOwnClick(event)) return;
    onOpenReport(report.id, { mode: "new-tab" });
  };

  /** Without this, a middle press starts the browser's autoscroll instead of opening a tab. */
  const handleRowMouseDown = (event: MouseEvent<HTMLTableRowElement>) => {
    if (event.button === MIDDLE_MOUSE_BUTTON) event.preventDefault();
  };

  return (
    <TableRow
      className={cn(
        "group/row",
        isClickable &&
          "cursor-pointer focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring",
      )}
      data-state={isPeeked ? "selected" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onMouseEnter={() => setIsRowHovered(true)}
      onMouseLeave={() => setIsRowHovered(false)}
      onClick={isClickable ? handleRowClick : undefined}
      onAuxClick={isClickable ? handleRowAuxClick : undefined}
      onMouseDown={isClickable ? handleRowMouseDown : undefined}
      onKeyDown={isClickable ? handleRowKeyDown : undefined}
      aria-label={
        isClickable
          ? t("preflight.pastReports.openReport", { name: report.name })
          : undefined
      }
    >
      <TableCell className="overflow-visible">
        <div
          className={cn(
            "flex min-w-0 items-center",
            isEditingName ? "gap-0" : "gap-3",
          )}
        >
          {!isEditingName ? <ReportFavoriteToggle report={report} /> : null}
          <div className="min-w-0 w-full flex-1">
            <ReportNameCell
              report={report}
              canRename={isTerminal}
              isRowHovered={isRowHovered}
              onEditingChange={setIsEditingName}
            />
          </div>
          {!isEditingName && status === "COMPLETED_WITH_ERRORS" ? (
            <PartialAnalysisMarker />
          ) : null}
        </div>
      </TableCell>
      <TableCell className="text-muted-foreground">
        <CreatedCell iso={report.createdAt} />
      </TableCell>
      <TableCell className="text-foreground tabular-nums">{assetCount}</TableCell>
      <TableCell className="text-foreground">
        <span className="block truncate text-sm" title={report.brandName}>
          {report.brandName}
        </span>
      </TableCell>
      <TableCell>
        <ReportMarketCell report={report} isRowHovered={isRowHovered} />
      </TableCell>
      <TableCell>
        <PlatformPlacementCell
          platform={report.platform}
          placement={report.placement}
        />
      </TableCell>
      <TableCell className="whitespace-nowrap">
        <AvgScoreCell status={status} score={score} band={band} />
      </TableCell>
      <TableCell>
        <PassFailCell
          passedCount={report.criteriaPassedCount}
          failedCount={report.criteriaFailedCount}
          isTerminal={isTerminal}
        />
      </TableCell>
      <TableCell>
        <OwnerCell owner={report.createdBy} />
      </TableCell>
      <TableCell
        className="relative bg-inherit p-0"
        onClick={(event) => event.stopPropagation()}
        onAuxClick={(event) => event.stopPropagation()}
      >
        <PastReportRowActions
          report={report}
          isRowHovered={isRowHovered}
          isTerminal={isTerminal}
          status={status}
          retryPending={retryMutation.isPending}
          exportPending={exportMutation.isPending}
          onOpenReport={onOpenReport}
          onPrefetchReport={prefetchReport}
          onRetry={() => retryMutation.mutate()}
          onExport={(format) => exportMutation.mutate(format)}
        />
      </TableCell>
    </TableRow>
  );
}

function LoadingRows() {
  return (
    <>
      {Array.from({ length: 4 }).map((_, index) => (
        <TableRow key={index}>
          {Array.from({ length: TABLE_COLUMN_COUNT }).map((__, cell) => (
            <TableCell key={cell}>
              <Skeleton className="h-4 w-full max-w-32 rounded" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

export interface PastReportsTableProps {
  onOpenReport: (reportId: string, options?: OpenPreflightReportOptions) => void;
  /** Report shown in the hub's side peek, highlighted as the selected row. */
  peekReportId?: string | null;
  /**
   * Openable report ids loaded so far, in the order the table shows them (current sort,
   * search and filters), and whether more pages exist. The side peek steps through this list.
   */
  onOpenableReportIdsChange?: (
    reportIds: readonly string[],
    hasMoreReports: boolean,
  ) => void;
  /**
   * Market scope from the hub header; `null` lists every market. A string[] is
   * an OR filter. The hub owns the scope so the table and the dashboard read
   * from one source (R8).
   */
  market?: string | readonly string[] | null;
  /**
   * Platform filter lifted to the hub so the Sources insight tab can set it.
   * When omitted, the table keeps its own local filter (stories / standalone use).
   */
  platform?: PreflightPlatformCode | undefined;
  onPlatformChange?: (platform: PreflightPlatformCode | undefined) => void;
}

/**
 * The Hub's Past Reports list: server-side search + platform filter within the
 * hub's market scope, infinite scroll, live per-row status polling, and
 * state-derived actions (past-reports R1–R7, generate-status R3–R10, R8).
 */
export function PastReportsTable({
  onOpenReport,
  peekReportId = null,
  onOpenableReportIdsChange,
  market = null,
  platform: platformProp,
  onPlatformChange,
}: PastReportsTableProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [localPlatform, setLocalPlatform] = useState<
    PreflightPlatformCode | undefined
  >();
  const platform = onPlatformChange != null ? platformProp : localPlatform;
  const setPlatform = onPlatformChange ?? setLocalPlatform;
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [sort, setSort] = useState<PastReportsSort>(INITIAL_SORT);

  const { debounced: applySearch, cancel: cancelSearch } = useDebouncedCallback(
    setSearch,
    SEARCH_DEBOUNCE_MS,
  );

  const query = usePreflightReports({
    search,
    platform,
    market,
    favoritesOnly,
    sortBy: sort.field,
    sortOrder: sort.order,
  });
  const { reports, hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  const hasMoreReports = hasNextPage ?? false;

  const openableReportIds = reports
    .filter((report) => isTerminalReportStatus(report.status))
    .map((report) => report.id);
  const openableReportIdsKey = openableReportIds.join(",");
  const lastOpenableReportId = openableReportIds.at(-1);
  useEffect(() => {
    onOpenableReportIdsChange?.(
      openableReportIdsKey ? openableReportIdsKey.split(",") : [],
      hasMoreReports,
    );
  }, [openableReportIdsKey, hasMoreReports]); // eslint-disable-line react-hooks/exhaustive-deps -- re-run on content, not callback identity

  /** Stepping the peek onto the last loaded report loads the next page, so J keeps going. */
  useEffect(() => {
    if (peekReportId == null || peekReportId !== lastOpenableReportId) return;
    if (!hasMoreReports || isFetchingNextPage) return;
    void fetchNextPage();
  }, [peekReportId, lastOpenableReportId, hasMoreReports, isFetchingNextPage, fetchNextPage]);
  /** Only the filters this table owns can be cleared from its empty state. */
  const ownFiltersActive =
    search.trim().length > 0 || platform != null || favoritesOnly;
  /** A market scope narrows the list too, so no results is a filtered empty state (R8). */
  const isNarrowed = ownFiltersActive || market != null;

  /** Having starred nothing yet reads differently from a search that found nothing. */
  function emptyStateCopy(): {
    icon: IconName;
    title: string;
    description: string;
  } {
    const isFavoritesEmpty =
      favoritesOnly && search.trim().length === 0 && platform == null;
    if (isFavoritesEmpty) {
      return {
        icon: "star",
        title: t("preflight.pastReports.emptyFavoritesTitle"),
        description: t("preflight.pastReports.emptyFavoritesDescription"),
      };
    }
    if (isNarrowed) {
      return {
        icon: "file-text",
        title: t("preflight.pastReports.emptyFilteredTitle"),
        description: t("preflight.pastReports.emptyFilteredDescription"),
      };
    }
    return {
      icon: "file-text",
      title: t("preflight.pastReports.emptyTitle"),
      description: t("preflight.pastReports.emptyDescription"),
    };
  }

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useInfiniteScroll(
    sentinelRef,
    scrollContainerRef,
    () => void query.fetchNextPage(),
    query.hasNextPage ?? false,
    query.isFetchingNextPage,
    {
      enabled: !query.isLoading && !query.isError,
      recheckKey: reports.length,
    },
  );

  function invalidateList() {
    void queryClient.invalidateQueries({ queryKey: preflightKeys.lists() });
  }

  function handleSearchChange(next: string) {
    setSearchInput(next);
    applySearch(next);
  }

  function handleSort(field: PreflightReportSortField) {
    setSort((current) => nextSort(current, field));
  }

  function clearFilters() {
    cancelSearch();
    setSearchInput("");
    setSearch("");
    setPlatform(undefined);
    setFavoritesOnly(false);
  }

  const headerToolbar = useMemo(
    () => (
      <div className="bg-card flex flex-col gap-3 border-b border-border px-4 py-3 sm:px-5 md:flex-row md:items-center md:justify-between md:gap-4">
        <div className="flex shrink-0 items-center gap-2">
          <Icon name="history" size="md" className="text-muted-foreground" />
          <h2 className="text-foreground text-sm font-semibold">
            {t("preflight.pastReports.title")}
          </h2>
          {!query.isLoading ? (
            <span className="text-muted-foreground bg-muted rounded px-1.5 py-0.5 text-[11px] tabular-nums">
              {reports.length}
            </span>
          ) : null}
        </div>

        <div className="flex w-full min-w-0 flex-col gap-3 md:w-auto md:flex-row md:items-center md:gap-2">
          <PlatformPicker
            includeAll
            value={platform ?? null}
            onChange={(next) => setPlatform(next ?? undefined)}
          />

          <Toggle
            variant="outline"
            size="sm"
            pressed={favoritesOnly}
            onPressedChange={setFavoritesOnly}
            aria-label={t("preflight.pastReports.filterFavoritesHint")}
            className="shrink-0 data-[state=on]:border-warning-border data-[state=on]:bg-warning-subtle data-[state=on]:text-warning-subtle-foreground"
          >
            <Icon
              name="star"
              size="xs"
              color="inherit"
              className={favoritesOnly ? "fill-current" : undefined}
            />
            {t("preflight.pastReports.filterFavorites")}
          </Toggle>

          <div className="relative w-full min-w-0 md:w-56">
            <Icon
              name="search"
              size="xs"
              className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 -translate-y-1/2"
            />
            <Input
              type="search"
              placeholder={t("preflight.pastReports.searchPlaceholder")}
              aria-label={t("preflight.pastReports.searchPlaceholder")}
              value={searchInput}
              onChange={(event) => handleSearchChange(event.target.value)}
              className="pl-8"
            />
          </div>
        </div>
      </div>
    ),
    [searchInput, platform, favoritesOnly, reports.length, query.isLoading, t], // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <div className="border-border bg-card flex flex-col overflow-hidden rounded-md border">
      {headerToolbar}

      <div
        ref={scrollContainerRef}
        className="max-h-[60vh] overflow-x-auto overflow-y-auto"
      >
        <TooltipProvider delayDuration={400}>
        <Table className={TABLE_LAYOUT}>
          <TableHeader className="bg-muted/30 [&_tr]:border-border">
            <TableRow className="border-border hover:bg-transparent">
              <SortableTableHead
                field="name"
                label={t("preflight.pastReports.columnReport")}
                sort={sort}
                onSort={handleSort}
                className={COLUMN_WIDTH.report}
              />
              <SortableTableHead
                field="createdAt"
                label={t("preflight.pastReports.columnCreated")}
                sort={sort}
                onSort={handleSort}
                className={COLUMN_WIDTH.created}
              />
              <SortableTableHead
                field="assetCount"
                label={t("preflight.pastReports.columnCreatives")}
                sort={sort}
                onSort={handleSort}
                className={COLUMN_WIDTH.creatives}
              />
              <SortableTableHead
                field="brandName"
                label={t("preflight.pastReports.columnBrand", "Brand")}
                sort={sort}
                onSort={handleSort}
                className={COLUMN_WIDTH.brand}
              />
              <SortableTableHead
                field="market"
                label={t("preflight.pastReports.columnMarket", "Market")}
                sort={sort}
                onSort={handleSort}
                className={COLUMN_WIDTH.market}
              />
              <TableHead className={cn(TABLE_HEAD, COLUMN_WIDTH.platformPlacement)}>
                {t("preflight.pastReports.columnPlatformPlacement")}
              </TableHead>
              <SortableTableHead
                field="overallScore"
                label={t("preflight.pastReports.columnAvgScore")}
                sort={sort}
                onSort={handleSort}
                className={COLUMN_WIDTH.avgScore}
              />
              <TableHead className={cn(TABLE_HEAD, COLUMN_WIDTH.passFail)}>
                {t("preflight.pastReports.columnPassFail")}
              </TableHead>
              <SortableTableHead
                field="owner"
                label={t("preflight.pastReports.columnOwner")}
                sort={sort}
                onSort={handleSort}
                className={COLUMN_WIDTH.owner}
              />
              <TableHead className={cn(TABLE_HEAD, COLUMN_WIDTH.actions, "text-right")}>
                {t("preflight.pastReports.columnActions")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {query.isLoading ? (
              <LoadingRows />
            ) : (
              reports.map((report) => (
                <PastReportRow
                  key={report.id}
                  report={report}
                  isPeeked={report.id === peekReportId}
                  onOpenReport={onOpenReport}
                  onTerminal={invalidateList}
                />
              ))
            )}
          </TableBody>
        </Table>
        </TooltipProvider>

        {!query.isLoading && query.isError && reports.length === 0 ? (
          <div className="flex flex-col items-center gap-3 px-4 py-10">
            <p className="text-muted-foreground text-sm">
              {t("preflight.pastReports.loadError")}
            </p>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              iconStart="refresh-cw"
              onClick={() => void query.refetch()}
            >
              {t("preflight.pastReports.retry")}
            </Button>
          </div>
        ) : null}

        {!query.isLoading && !query.isError && reports.length === 0 ? (
          <EmptyState
            {...emptyStateCopy()}
            action={
              ownFiltersActive ? (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={clearFilters}
                >
                  {t("preflight.pastReports.clearFilters")}
                </Button>
              ) : undefined
            }
          />
        ) : null}

        <div ref={sentinelRef} className="h-1">
          {query.isFetchingNextPage ? (
            <p className="text-muted-foreground py-3 text-center text-xs">
              {t("preflight.pastReports.loadingMore")}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
